import React, { useState, useEffect } from 'react';
import AutoTranslateText from '../components/AutoTranslateText';
import { PluginInterface, PluginConfigProps, PluginPreviewProps } from '../types/PluginInterface';
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';
import { ALL_COUNTRIES } from '../utils/countries';
import { usePaymentApiKeys } from '../hooks/usePaymentApiKeys';

// Mapping des villes principales par pays
const CITIES_BY_COUNTRY: Record<string, Array<{value: string, label: string}>> = {
  FR: [
    { value: 'Paris', label: 'Paris' },
    { value: 'Lyon', label: 'Lyon' },
    { value: 'Marseille', label: 'Marseille' },
    { value: 'Toulouse', label: 'Toulouse' },
    { value: 'Nice', label: 'Nice' },
    { value: 'Nantes', label: 'Nantes' },
    { value: 'Strasbourg', label: 'Strasbourg' },
    { value: 'Montpellier', label: 'Montpellier' },
    { value: 'Bordeaux', label: 'Bordeaux' },
    { value: 'Lille', label: 'Lille' }
  ],
  GB: [
    { value: 'London', label: 'Londres' },
    { value: 'Birmingham', label: 'Birmingham' },
    { value: 'Manchester', label: 'Manchester' },
    { value: 'Liverpool', label: 'Liverpool' },
    { value: 'Edinburgh', label: 'Édimbourg' },
    { value: 'Glasgow', label: 'Glasgow' }
  ],
  DE: [
    { value: 'Berlin', label: 'Berlin' },
    { value: 'Munich', label: 'Munich' },
    { value: 'Frankfurt', label: 'Francfort' },
    { value: 'Hamburg', label: 'Hambourg' },
    { value: 'Cologne', label: 'Cologne' },
    { value: 'Stuttgart', label: 'Stuttgart' }
  ],
  ES: [
    { value: 'Madrid', label: 'Madrid' },
    { value: 'Barcelona', label: 'Barcelone' },
    { value: 'Valencia', label: 'Valence' },
    { value: 'Seville', label: 'Séville' },
    { value: 'Bilbao', label: 'Bilbao' },
    { value: 'Malaga', label: 'Malaga' }
  ],
  IT: [
    { value: 'Rome', label: 'Rome' },
    { value: 'Milan', label: 'Milan' },
    { value: 'Naples', label: 'Naples' },
    { value: 'Turin', label: 'Turin' },
    { value: 'Florence', label: 'Florence' },
    { value: 'Venice', label: 'Venise' }
  ],
  US: [
    { value: 'New York', label: 'New York' },
    { value: 'Los Angeles', label: 'Los Angeles' },
    { value: 'Chicago', label: 'Chicago' },
    { value: 'Houston', label: 'Houston' },
    { value: 'Phoenix', label: 'Phoenix' },
    { value: 'Philadelphia', label: 'Philadelphie' },
    { value: 'San Francisco', label: 'San Francisco' },
    { value: 'Seattle', label: 'Seattle' },
    { value: 'Miami', label: 'Miami' },
    { value: 'Boston', label: 'Boston' }
  ],
  CA: [
    { value: 'Toronto', label: 'Toronto' },
    { value: 'Montreal', label: 'Montréal' },
    { value: 'Vancouver', label: 'Vancouver' },
    { value: 'Calgary', label: 'Calgary' },
    { value: 'Ottawa', label: 'Ottawa' },
    { value: 'Quebec City', label: 'Québec' }
  ],
  JP: [
    { value: 'Tokyo', label: 'Tokyo' },
    { value: 'Osaka', label: 'Osaka' },
    { value: 'Kyoto', label: 'Kyoto' },
    { value: 'Yokohama', label: 'Yokohama' },
    { value: 'Nagoya', label: 'Nagoya' },
    { value: 'Sapporo', label: 'Sapporo' }
  ]
};

// Configuration par défaut du plugin météo
const defaultWeatherConfig = {
  id: undefined,
  title: 'Météo',
  city: 'Paris',
  country: 'FR',
  units: 'metric',
  displayMode: 'current',
  forecastDays: 5,
  showDetails: true,
  showIcon: true,
  showTemperature: true,
  showHumidity: true,
  showWind: true,
  showPressure: false,
  backgroundColor: '#ffffff',
  textColor: '#333333',
  borderRadius: 8,
  showBorder: true,
  borderColor: '#dee2e6',
  refreshInterval: 30
};

// Interface pour les données météo
interface WeatherData {
  location: string;
  temperature: number;
  description: string;
  icon: string;
  humidity: number;
  windSpeed: number;
  pressure: number;
  feelsLike: number;
}

interface ForecastData {
  date: string;
  temperature: { min: number; max: number };
  description: string;
  icon: string;
}

// Composant de configuration
const WeatherConfigComponent: React.FC<PluginConfigProps> = ({ config, onConfigChange, t }) => {
  const [loading, setLoading] = useState(false);
  const { openWeatherApiKey, hasOpenWeatherKey, isLoading: isApiKeyLoading } = usePaymentApiKeys();
  const [availableCities, setAvailableCities] = useState<Array<{value: string, label: string}>>(
    CITIES_BY_COUNTRY[config.country || 'FR'] || []
  );

  const updateConfig = (updates: any) => {
    onConfigChange({ ...config, ...updates });
  };

  // Mettre à jour les villes disponibles quand le pays change
  useEffect(() => {
    const cities = CITIES_BY_COUNTRY[config.country || 'FR'] || [];
    setAvailableCities(cities);
    
    // Si la ville actuelle n'est pas dans le nouveau pays, prendre la première ville disponible
    if (cities.length > 0 && !cities.find(c => c.value === config.city)) {
      updateConfig({ city: cities[0].value });
    }
  }, [config.country]);

  // Test réel de l'API OpenWeather
  const testWeatherAPI = async () => {
    if (!hasOpenWeatherKey) {
      alert('Aucune clé API OpenWeather configurée dans votre profil.');
      return;
    }

    const cityRaw = (config.city || '').trim();
    if (!cityRaw) {
        alert('Veuillez entrer un nom de ville.');
        return;
    }

    setLoading(true);

    try {
      const query = config.country 
        ? `${encodeURIComponent(cityRaw)},${encodeURIComponent(config.country)}`
        : encodeURIComponent(cityRaw);

      const response = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?q=${query}&units=${config.units}&appid=${openWeatherApiKey?.trim()}&lang=fr`
      );
      
      if (!response.ok) {
        if (response.status === 404) {
             throw new Error(`Ville introuvable : "${cityRaw}". Essayez de modifier l'orthographe (ex: "Saint-François" au lieu de "st François").`);
        }
        throw new Error(`Erreur API: ${response.status}`);
      }
      
      const data = await response.json();
      
      const weather: WeatherData = {
        location: `${data.name}, ${data.sys.country}`,
        temperature: Math.round(data.main.temp),
        description: data.weather[0].description,
        icon: `https://openweathermap.org/img/wn/${data.weather[0].icon}@2x.png`,
        humidity: data.main.humidity,
        windSpeed: Math.round(data.wind.speed * 3.6),
        pressure: data.main.pressure,
        feelsLike: Math.round(data.main.feels_like)
      };
      
      updateConfig({ cachedData: weather });
      
    } catch (error: any) {
      console.error('Erreur test météo:', error);
      alert(error.message || 'Erreur lors du test de la configuration.');
    } finally {
      setLoading(false);
    }
  };

  const countries = ALL_COUNTRIES.map((country) => ({
    value: country.code,
    label: country.label
  }));

  const testWeather = config.cachedData;

  // Style helper pour les sections
  const sectionTitleStyle = {
      fontSize: '0.9rem',
      fontWeight: '600',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      color: '#6c757d',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
  };

  return (
    <div className="weather-config p-2">
      {/* Alerte si pas de clé API */}
      {!isApiKeyLoading && !hasOpenWeatherKey && (
        <div className="alert alert-warning shadow-sm border-warning">
          <div className="d-flex">
            <div className="me-3" style={{ fontSize: '1.5rem' }}>⚠️</div>
            <div>
                <strong><AutoTranslateText sourceLang="fr">Clé API manquante</AutoTranslateText></strong>
                <p className="mb-0 mt-1 small">
                    Ajoutez votre clé OpenWeather dans <strong><AutoTranslateText sourceLang="fr">Profil &gt; Clés API</AutoTranslateText></strong>.
                </p>
            </div>
          </div>
        </div>
      )}

      {/* Titre principal */}
      <div className="mb-4">
          <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">Titre du Widget</AutoTranslateText></label>
          <input
            type="text"
            className="form-control form-control-lg"
            value={config.title || ''}
            onChange={(e) => updateConfig({ title: e.target.value })}
            placeholder="Météo"
          />
      </div>

      {/* Section 1: Localisation */}
      <div className="card mb-4 border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="card-body">
            <div style={sectionTitleStyle}>
                <span>🌍</span> Localisation
            </div>
            <div className="row g-3">
                <div className="col-md-5">
                <label className="form-label small text-muted"><AutoTranslateText sourceLang="fr">Pays</AutoTranslateText></label>
                <select
                    className="form-select"
                    value={config.country || 'FR'}
                    onChange={(e) => updateConfig({ country: e.target.value })}
                >
                    {countries.map(country => (
                    <option key={country.value} value={country.value}>
                        {country.label}
                    </option>
                    ))}
                </select>
                </div>
                <div className="col-md-7">
                <label className="form-label small text-muted"><AutoTranslateText sourceLang="fr">Ville</AutoTranslateText></label>
                <div className="input-group">
                    <select
                    className="form-select"
                    style={{ maxWidth: '40%' }}
                    value={config.city || ''}
                    onChange={(e) => updateConfig({ city: e.target.value })}
                    >
                    <option value=""><AutoTranslateText sourceLang="fr">Liste...</AutoTranslateText></option>
                    {availableCities.map(city => (
                        <option key={city.value} value={city.value}>
                        {city.label}
                        </option>
                    ))}
                    </select>
                    <input
                    type="text"
                    className="form-control"
                    value={config.city || ''}
                    onChange={(e) => updateConfig({ city: e.target.value })}
                    placeholder="Tapez une ville..."
                    />
                </div>
                </div>
            </div>
        </div>
      </div>

      <div className="row">
          {/* Section 2: Paramètres d'affichage */}
          <div className="col-lg-6 mb-4">
            <div className="card h-100 border-0 shadow-sm">
                <div className="card-body">
                    <div style={sectionTitleStyle}>
                        <span>⚙️</span> Paramètres
                    </div>
                    
                    <div className="mb-3">
                        <label className="form-label small text-muted"><AutoTranslateText sourceLang="fr">Unités</AutoTranslateText></label>
                        <select
                            className="form-select"
                            value={config.units || 'metric'}
                            onChange={(e) => updateConfig({ units: e.target.value })}
                        >
                            <option value="metric"><AutoTranslateText sourceLang="fr">Celsius (°C)</AutoTranslateText></option>
                            <option value="imperial"><AutoTranslateText sourceLang="fr">Fahrenheit (°F)</AutoTranslateText></option>
                            <option value="kelvin"><AutoTranslateText sourceLang="fr">Kelvin (K)</AutoTranslateText></option>
                        </select>
                    </div>

                    <div className="mb-3">
                        <label className="form-label small text-muted"><AutoTranslateText sourceLang="fr">Mode d'affichage</AutoTranslateText></label>
                        <select
                            className="form-select"
                            value={config.displayMode || 'current'}
                            onChange={(e) => updateConfig({ displayMode: e.target.value })}
                        >
                            <option value="current"><AutoTranslateText sourceLang="fr">Météo actuelle</AutoTranslateText></option>
                            <option value="forecast"><AutoTranslateText sourceLang="fr">Prévisions</AutoTranslateText></option>
                            <option value="both"><AutoTranslateText sourceLang="fr">Actuel + Prévisions</AutoTranslateText></option>
                        </select>
                    </div>

                    <div className="mb-3">
                        <label className="form-label small text-muted"><AutoTranslateText sourceLang="fr">Jours de prévision</AutoTranslateText></label>
                        <select
                            className="form-select"
                            value={config.forecastDays || 5}
                            onChange={(e) => updateConfig({ forecastDays: parseInt(e.target.value) })}
                            disabled={config.displayMode === 'current'}
                        >
                            <option value={3}><AutoTranslateText sourceLang="fr">3 jours</AutoTranslateText></option>
                            <option value={5}><AutoTranslateText sourceLang="fr">5 jours</AutoTranslateText></option>
                            <option value={7}><AutoTranslateText sourceLang="fr">7 jours</AutoTranslateText></option>
                        </select>
                    </div>
                </div>
            </div>
          </div>

          {/* Section 3: Visibilité */}
          <div className="col-lg-6 mb-4">
             <div className="card h-100 border-0 shadow-sm">
                <div className="card-body">
                    <div style={sectionTitleStyle}>
                        <span>👁️</span> Éléments visibles
                    </div>
                    <div className="d-flex flex-column gap-2">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" checked={config.showIcon !== false} onChange={(e) => updateConfig({ showIcon: e.target.checked })} />
                            <label className="form-check-label"><AutoTranslateText sourceLang="fr">Icône météo</AutoTranslateText></label>
                        </div>
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" checked={config.showTemperature !== false} onChange={(e) => updateConfig({ showTemperature: e.target.checked })} />
                            <label className="form-check-label"><AutoTranslateText sourceLang="fr">Température</AutoTranslateText></label>
                        </div>
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" checked={config.showDetails !== false} onChange={(e) => updateConfig({ showDetails: e.target.checked })} />
                            <label className="form-check-label"><AutoTranslateText sourceLang="fr">Détails (Vent, Humidité...)</AutoTranslateText></label>
                        </div>
                        
                        {config.showDetails !== false && (
                            <div className="ms-4 ps-2 border-start d-flex flex-column gap-1">
                                <small className="text-muted d-block mb-1"><AutoTranslateText sourceLang="fr">Sous-Détails :</AutoTranslateText></small>
                                <div className="form-check">
                                    <input className="form-check-input" type="checkbox" checked={config.showHumidity !== false} onChange={(e) => updateConfig({ showHumidity: e.target.checked })} />
                                    <label className="form-check-label small"><AutoTranslateText sourceLang="fr">Humidité</AutoTranslateText></label>
                                </div>
                                <div className="form-check">
                                    <input className="form-check-input" type="checkbox" checked={config.showWind !== false} onChange={(e) => updateConfig({ showWind: e.target.checked })} />
                                    <label className="form-check-label small"><AutoTranslateText sourceLang="fr">Vent</AutoTranslateText></label>
                                </div>
                                <div className="form-check">
                                    <input className="form-check-input" type="checkbox" checked={config.showPressure === true} onChange={(e) => updateConfig({ showPressure: e.target.checked })} />
                                    <label className="form-check-label small"><AutoTranslateText sourceLang="fr">Pression</AutoTranslateText></label>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
             </div>
          </div>
      </div>

      {/* Section 4: Apparence */}
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body">
            <div style={sectionTitleStyle}>
                <span>🎨</span> Apparence
            </div>
            <div className="row align-items-end g-3">
                <div className="col-4">
                    <label className="form-label small text-muted"><AutoTranslateText sourceLang="fr">Fond</AutoTranslateText></label>
                    <input
                        type="color"
                        className="form-control form-control-color w-100"
                        value={config.backgroundColor || '#ffffff'}
                        onChange={(e) => updateConfig({ backgroundColor: e.target.value })}
                        title="Couleur de fond"
                    />
                </div>
                <div className="col-4">
                    <label className="form-label small text-muted"><AutoTranslateText sourceLang="fr">Texte</AutoTranslateText></label>
                    <input
                        type="color"
                        className="form-control form-control-color w-100"
                        value={config.textColor || '#333333'}
                        onChange={(e) => updateConfig({ textColor: e.target.value })}
                        title="Couleur du texte"
                    />
                </div>
                <div className="col-4">
                    <div className="form-check form-switch mb-2">
                        <input
                        className="form-check-input"
                        type="checkbox"
                        checked={config.showBorder !== false}
                        onChange={(e) => updateConfig({ showBorder: e.target.checked })}
                        />
                        <label className="form-check-label small"><AutoTranslateText sourceLang="fr">Bordure</AutoTranslateText></label>
                    </div>
                </div>
            </div>
            
            <div className="row mt-3">
                <div className="col-12">
                   <label className="form-label small text-muted d-flex justify-content-between">
                       <span><AutoTranslateText sourceLang="fr">Rayon des coins</AutoTranslateText></span>
                       <span>{config.borderRadius || 8}px</span>
                   </label>
                   <input
                        type="range"
                        className="form-range"
                        min="0"
                        max="20"
                        value={config.borderRadius || 8}
                        onChange={(e) => updateConfig({ borderRadius: parseInt(e.target.value) })}
                    />
                </div>
            </div>
        </div>
      </div>

      {/* Section 5: Test & Action */}
      <div className="d-grid gap-2">
          {!config.city ? (
               <button className="btn btn-secondary" disabled>
                   Choisissez une ville pour tester
               </button>
          ) : (
                <button
                    type="button"
                    className={`btn ${testWeather ? 'btn-success' : 'btn-primary'} btn-lg`}
                    onClick={testWeatherAPI}
                    disabled={loading || !hasOpenWeatherKey}
                >
                    {loading ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Connexion à OpenWeather...
                    </>
                    ) : (
                        testWeather ? '✅ Config mise à jour (Re-tester)' : '🔄 Tester & Mettre à jour l\'aperçu'
                    )}
                </button>
          )}
          
          {testWeather && (
             <div className="text-center text-success small">
                 <i className="fas fa-check-circle me-1"></i>
                 Dernier test réussi pour : {testWeather.location}
             </div>
          )}
      </div>
    </div>
  );
};

// Composant de prévisualisation
const WeatherPreviewComponent: React.FC<PluginPreviewProps> = ({ config, t }) => {
  // Utiliser les données du test si disponibles, sinon utiliser des données factices par défaut
  const realData = config.cachedData;

  const displayData: WeatherData = realData || {
    location: `${config.city || 'Paris'}, ${config.country || 'FR'}`,
    temperature: 22,
    description: 'Ensoleillé (simulation)',
    icon: 'https://openweathermap.org/img/wn/01d@2x.png', // Utiliser une vraie URL d'icône pour être cohérent
    humidity: 65,
    windSpeed: 12,
    pressure: 1013,
    feelsLike: 24
  };

  // ✅ Liste étendue à 7 jours pour prendre en charge l'option "7 jours"
  const mockForecast: ForecastData[] = [
    { date: 'Lun', temperature: { min: 18, max: 25 }, description: 'Ensoleillé', icon: '☀️' },
    { date: 'Mar', temperature: { min: 16, max: 22 }, description: 'Nuageux', icon: '☁️' },
    { date: 'Mer', temperature: { min: 19, max: 26 }, description: 'Partiellement nuageux', icon: '⛅' },
    { date: 'Jeu', temperature: { min: 15, max: 20 }, description: 'Pluvieux', icon: '🌧️' },
    { date: 'Ven', temperature: { min: 17, max: 23 }, description: 'Ensoleillé', icon: '☀️' },
    { date: 'Sam', temperature: { min: 16, max: 21 }, description: 'Averses', icon: '🌦️' },
    { date: 'Dim', temperature: { min: 18, max: 24 }, description: 'Ensoleillé', icon: '☀️' }
  ];

  const getTemperatureUnit = () => {
    switch (config.units) {
      case 'imperial': return '°F';
      case 'kelvin': return 'K';
      default: return '°C';
    }
  };

  const containerStyle = {
    backgroundColor: config.backgroundColor || '#ffffff',
    color: config.textColor || '#333333',
    borderRadius: `${config.borderRadius || 8}px`,
    border: config.showBorder !== false ? `1px solid ${config.borderColor || '#dee2e6'}` : 'none',
    padding: '16px',
    fontFamily: 'Arial, sans-serif'
  };

  // ✅ Style normalisé pour les textes secondaires en utilisant la couleur du texte choisie avec opacité
  const secondaryTextStyle = {
      color: config.textColor || '#333333',
      opacity: 0.7
  };

  return (
    <div style={containerStyle}>
      <div className="text-center mb-3">
        <h6 className="mb-1" style={{ color: config.textColor || '#333333' }}>
          {config.title || 'Météo'}
        </h6>
        <small style={secondaryTextStyle}>{displayData.location}</small>
      </div>

      {(config.displayMode === 'current' || config.displayMode === 'both') && (
        <div className="text-center mb-3">
          {config.showIcon !== false && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                {realData ? (
                    <img src={displayData.icon} alt="météo" style={{ width: 80, height: 80 }} />
                ) : (
                    <div style={{ fontSize: '3rem', lineHeight: 1 }}>☀️</div>
                )}
            </div>
          )}
          
          {config.showTemperature !== false && (
            <div className="fs-2 fw-bold mb-1" style={{ color: config.textColor || '#333333' }}>
              {displayData.temperature}{getTemperatureUnit()}
            </div>
          )}
          
          <div className="mb-2" style={secondaryTextStyle}>{displayData.description}</div>
          
          {config.showDetails !== false && (
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              {config.showHumidity !== false && (
                <small style={{ color: config.textColor || '#333333' }}>
                  <span className="text-primary">💧</span> {displayData.humidity}%
                </small>
              )}
              {config.showWind !== false && (
                <small style={{ color: config.textColor || '#333333' }}>
                  <span className="text-info">💨</span> {displayData.windSpeed} km/h
                </small>
              )}
              {config.showPressure === true && (
                <small style={{ color: config.textColor || '#333333' }}>
                  <span className="text-warning">📊</span> {displayData.pressure} hPa
                </small>
              )}
            </div>
          )}
        </div>
      )}

      {(config.displayMode === 'forecast' || config.displayMode === 'both') && (
        <div>
          {config.displayMode === 'both' && <hr style={{ borderColor: config.borderColor || '#dee2e6' }} />}
          <div className="row g-1">
            {/* ✅ Utilisation correcte du nombre de jours configuré (jusqu'à 7) */}
            {mockForecast.slice(0, config.forecastDays || 5).map((day, index) => (
              <div key={index} className="col text-center">
                <small className="d-block" style={secondaryTextStyle}>{day.date}</small>
                <div style={{ fontSize: '1.2rem' }}>{day.icon}</div>
                <small className="fw-bold d-block" style={{ color: config.textColor || '#333333' }}>
                  {day.temperature.max}°
                </small>
                <small style={secondaryTextStyle}>
                  {day.temperature.min}°
                </small>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center mt-2">
        <small style={secondaryTextStyle}>
          {realData ? 'Données réelles' : `Mis à jour il y a ${config.refreshInterval || 30} min`}
        </small>
      </div>
    </div>
  );
};

// Plugin Météo Modulaire
const WeatherPluginModular: PluginInterface = {
  metadata: {
    id: 'weather-modular',
    name: 'Widget Météo',
    description: 'Affiche la météo actuelle et les prévisions pour une ville donnée via OpenWeather',
    version: '1.0.0',
    author: 'BookEditor',
    category: 'utility',
    icon: '🌤️',
    tags: ['météo', 'weather', 'prévisions', 'climat', 'température', 'openweather']
  },

  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  documentationForAI: {
    description: "Widget météo connectée API OpenWeather. Affiche conditions actuelles et/ou prévisions.",
    usage: "Utilise ce plugin pour afficher la météo d'une destination touristique ou d'un lieu d'événement.",
    
    // ✅ Schéma enrichi pour permettre à l'IA de contrôler le style et la visibilité
    configSchema: {
      type: "object",
      properties: {
        title: { type: "string", default: "Météo" },
        city: { type: "string", description: "Nom de la ville (ex: 'Paris', 'New York')" },
        country: { type: "string", description: "Code ISO 2 lettres (ex: 'FR', 'US', 'ES', 'IT')" },
        units: { type: "string", enum: ["metric", "imperial", "kelvin"], default: "metric" },
        
        // Modes d'affichage
        displayMode: { type: "string", enum: ["current", "forecast", "both"], description: "Actuel, Prévisions ou Les deux" },
        forecastDays: { type: "number", enum: [3, 5, 7], description: "Seulement si displayMode != current" },
        
        // Visibilité (Booléens)
        showDetails: { type: "boolean", description: "Afficher humidité/vent" },
        showIcon: { type: "boolean" },
        showTemperature: { type: "boolean" },
        
        // Style
        backgroundColor: { type: "string (hex)", description: "Couleur de fond" },
        textColor: { type: "string (hex)", description: "Couleur du texte" },
        showBorder: { type: "boolean" },
        borderRadius: { type: "number", description: "Arrondi (0-20)" }
      }
    } as any, // Cast nécessaire pour le schéma complexe
    
    exampleConfig: {
      title: "Météo en direct",
      city: "Marseille",
      country: "FR",
      units: "metric",
      displayMode: "both",
      forecastDays: 5,
      backgroundColor: "#e3f2fd", // Bleu clair pour l'exemple
      showDetails: true,
      borderRadius: 12
    }
  },
  
  ConfigComponent: WeatherConfigComponent,
  PreviewComponent: WeatherPreviewComponent,
  defaultConfig: defaultWeatherConfig,
  
  generateHTML: (config: any) => {
    const instanceId = config.id || `weather-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    // ✅ On nettoie config.cachedData avant de générer le HTML final pour forcer le Renderer
    // à récupérer les données fraîches "du jour" lors de l'appel par l'utilisateur final.
    const { cachedData, ...cleanConfig } = config;
    const finalConfig = { ...defaultWeatherConfig, ...cleanConfig, id: instanceId };

    return generateUniformPlaceholder({
      title: finalConfig.title || 'Widget Météo',
      description: `Météo pour ${finalConfig.city || 'Paris'}, ${finalConfig.country || 'FR'}`,
      icon: '🌤️',
      category: 'utility',
      config: finalConfig,
      pluginId: instanceId,
      pluginType: 'weather-modular'
    });
  },
  
  canInsert: async () => {
    return true;
  }
};

export default WeatherPluginModular;
